/**
 * Helpers for slimming and retaining userProfile access data in localStorage.
 */

const ACCESS_HISTORY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ACCESS_HISTORY_ENTRIES = 100;

const DISPLAY_FIELDS = [
  'id',
  'access_key',
  'asset_name',
  'asset_make',
  'asset_model',
  'street_address',
  'city',
  'status',
  'access_duration_until',
  'expiry_date',
  'updated_at',
  'history_archived_at',
];

function parseTime(value) {
  if (value == null || value === '') return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

function getAccessExpiryTime(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return parseTime(entry.access_duration_until ?? entry.expiry_date);
}

function getHistoryFallbackTime(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return (
    parseTime(entry.history_archived_at) ??
    parseTime(entry.updated_at)
  );
}

function isAccessHistoryExpired(entry) {
  const expiry = getAccessExpiryTime(entry);
  if (expiry != null) {
    return Date.now() > expiry + ACCESS_HISTORY_RETENTION_MS;
  }

  const fallback = getHistoryFallbackTime(entry);
  if (fallback != null) {
    return Date.now() > fallback + ACCESS_HISTORY_RETENTION_MS;
  }

  return false;
}

export function pruneAccessHistory(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.filter((entry) => !isAccessHistoryExpired(entry));
}

function countOpenTasks(tasks) {
  if (!tasks || typeof tasks !== 'object') return 0;
  return Object.values(tasks).filter((task) => task && !task.completed_at).length;
}

export function slimAccessForPersist(access, accessKey) {
  if (!access || typeof access !== 'object') return access;

  const key = access.access_key ?? accessKey;
  const slim = { access_key: key };

  DISPLAY_FIELDS.forEach((field) => {
    if (access[field] != null && access[field] !== '') {
      slim[field] = access[field];
    }
  });

  slim._open_task_count = countOpenTasks(access.tasks);

  return slim;
}

function slimAccessForHistory(entry) {
  if (!entry || typeof entry !== 'object') return entry;

  const slim = slimAccessForPersist(entry, entry.access_key);
  slim.history_archived_at = new Date().toISOString();
  return slim;
}

export function appendAccessHistory(currentHistory, entry) {
  const slimmed = slimAccessForHistory(entry);
  const accessKey = slimmed?.access_key;
  const withoutDuplicate = (currentHistory || []).filter(
    (existing) => !accessKey || existing?.access_key !== accessKey
  );
  const next = [...withoutDuplicate, slimmed];
  const pruned = pruneAccessHistory(next);
  if (pruned.length > MAX_ACCESS_HISTORY_ENTRIES) {
    return pruned.slice(-MAX_ACCESS_HISTORY_ENTRIES);
  }
  return pruned;
}

export function slimAccessesMap(accesses) {
  if (!accesses || typeof accesses !== 'object') return {};
  const out = {};
  Object.entries(accesses).forEach(([key, value]) => {
    out[key] = slimAccessForPersist(value, key);
  });
  return out;
}

export function emergencySlimProfile(profile) {
  if (!profile || typeof profile !== 'object') return profile;
  return {
    ...profile,
    accesses: slimAccessesMap(profile.accesses),
    access_history: pruneAccessHistory(
      (profile.access_history || []).map((entry) => slimAccessForHistory(entry))
    ),
  };
}
